import styled from 'styled-components';
import { SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { JSX } from 'react';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { ControlType } from '../../../../Form';
import type { Localization } from '../../../../../common/Localization';
import FormatDecorated from './FormatDecorated';
import { formatPrice } from '../../../../../utils/formatting';
import DeferredPaymentsMessage from '../../../../DeferredPayments/DeferredPaymentsMessage';
import type { CommonProps } from '../CommonProps';
import { getMaxPrice } from './priceUtils.ts';

const Diagram = styled.div`
  position: absolute;
  height: 50px;
  top: -42px;
  left: 10px;
  width: calc(100% - 20px);
  display: flex;
  flex-direction: row;
  gap: 1px;
  align-items: flex-end;
`;

const Column = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
`;

type GetField = {
  t: TranslationFunction;
  localization: Localization;
  fieldName?: string;
  searchMode: SearchModes;
  price: {
    min: number;
    max: number;
  };
  dynamicPrice: {
    diagram: number[];
    from: number;
    to: number;
    interval: number;
  };
  commonProps: CommonProps;
};

const getWidth = (commonProps: CommonProps) => {
  const { isSidebar, isMobile, isHorizontal } = commonProps;
  if (isSidebar) return 260;
  if (isHorizontal) return 268;
  if (!isMobile && !isHorizontal && !isSidebar) return 576;
  if (isMobile && canUseDOM) return window.innerWidth - 64;
  return 0;
};

const getRoundTo = (min: number, max: number) => {
  if (max - min <= 50) return 1;
  if (max - min <= 500) return 10;
  return 50;
};

const getFieldDynamic = ({ localization, fieldName, searchMode, price, dynamicPrice, commonProps, t }: GetField) => {
  const {
    lang,
    currency,
    deferredPaymentLink,
    configValuesApp: { isDeferredPaymentsEnabled },
  } = localization;
  const maxPriceSlider = formatPrice({ price: getMaxPrice(lang, currency), localization });
  const formatPriceRange = ({ minValue, maxValue }: { minValue: number; maxValue: number }): string | JSX.Element => {
    const min = formatPrice({ price: minValue, localization });
    const max = formatPrice({ price: maxValue, localization });

    return (
      <FormatDecorated
        min={min}
        max={max}
        minPrice={t('vacation.oss.filters.budget.min_price')}
        maxPrice={t('vacation.oss.filters.budget.max_price')}
        maxPriceSlider={maxPriceSlider.toString()}
      />
    );
  };
  const maxDiagramItem = Math.max(...dynamicPrice.diagram);
  const { isWaiting } = commonProps;
  const width = getWidth(commonProps);
  const roundTo = getRoundTo(dynamicPrice.from, dynamicPrice.to);

  return [
    ...(isDeferredPaymentsEnabled && deferredPaymentLink
      ? [
          {
            control: ControlType.LABEL,
            name: 'deferredPaymentsMessage',
            content: <DeferredPaymentsMessage {...{ localization, link: deferredPaymentLink }} />,
          },
        ]
      : []),
    {
      control: ControlType.SLIDER,
      name: fieldName || 'price',
      value: {
        min: price.min,
        max: price.max,
      },
      default: {
        min: dynamicPrice.from,
        max: dynamicPrice.to,
      },
      minAllowed: dynamicPrice.from,
      maxAllowed: dynamicPrice.to,
      bulletSize: 20,
      valueLabel: formatPriceRange,
      roundTo,
      width,
      className: 'slider-price',
      label:
        searchMode === SearchModes.DP
          ? t('vacation.oss.components.searchfilters.labels.price_person')
          : t('vacation.oss.components.searchfilters.labels.total_price'),
      valuesOnBottom: true,
      diagram: (
        <Diagram>
          {dynamicPrice.diagram.map((x, i) => {
            let height = x === maxDiagramItem ? 50 : (x * 50) / maxDiagramItem;
            if (height < 4) {
              height = 4;
            }
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Column key={`${x}_${i}`} style={{ height, width: `calc(100% / ${dynamicPrice.diagram.length})` }} />
            );
          })}
        </Diagram>
      ),
      ...(isWaiting || dynamicPrice.from === dynamicPrice.to ? { disabled: true } : {}),
    },
  ];
};

export default getFieldDynamic;
